import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EstimatedBalance, PlatformKey, StoreResponse, UpcomingPayout } from '@clarity/contract';
import { useDerivedSummary } from '../queries/index.js';
import { isOnboardingRequired, money, shortDate } from './format.js';
import { upcomingNotes } from './upcoming-notes.js';
import { splitOnboarded } from './combined/shared.js';
import {
  sumEstimatedBalances,
  type EstimatedBalanceAggregate,
} from '../lib/estimatedBalance.js';

/**
 * The estimated-balance LEAD of the dashboard summary band (clarity-daily-update
 * Req 4/5): the owner's headline "money still coming" figure, shown first on the
 * dashboard because it is the number they open it for. Distinct from the payouts
 * panel's {@link EstimatedBalanceCard} — this is the compact, scope-aware lead
 * that takes over the band's lead slot while Branches/Enabled/Onboarded become
 * the supporting trio.
 *
 * Scope-aware, mirroring the combined surfaces' fan-out (Req 5.5):
 *  - ONE onboarded branch → read that branch's `estimatedBalance` off its derive
 *    summary and render the full breakdown (confirmed / earned-since / less ads),
 *    so the ads deduction is visible here too.
 *  - MANY branches → fan out a hidden {@link BranchBalanceSource} per onboarded
 *    branch, fold with {@link sumEstimatedBalances}, and render the summed
 *    headline plus a "+ N couldn't be estimated" caveat.
 *
 * Only ONBOARDED branches contribute (an un-onboarded branch has no statement, so
 * its read would 409); the rest are not counted against the estimate. Each
 * per-branch read is a cheap DB read sharing the `derivedSummary` query cache
 * with the Onboarding/store surfaces — no portal fetch.
 */
export function DashboardBalance({ branches }: { branches: StoreResponse[] }) {
  const onboarded = useMemo(() => splitOnboarded(branches).onboarded, [branches]);

  // Collect each onboarded branch's estimate as its hidden source resolves. A
  // branch that is still loading / 409 / errored simply isn't present yet.
  const [byBranch, setByBranch] = useState<Record<string, BranchBalance>>({});
  const report = useCallback((id: string, result: BranchBalance) => {
    setByBranch((prev) => {
      const existing = prev[id];
      if (existing && existing.loading === result.loading && existing.estimate === result.estimate) {
        return prev;
      }
      return { ...prev, [id]: result };
    });
  }, []);

  // The currency comes from the branches (all a client's stores share one in
  // practice); take the first that carries one.
  const currency = onboarded.find((b) => b.profile.currency)?.profile.currency;

  const single = onboarded.length === 1 ? (onboarded[0] ?? null) : null;

  // Multi-branch: fold the resolved per-branch estimates into one aggregate.
  const estimates = useMemo(
    () => onboarded.map((b) => byBranch[b.id]?.estimate).filter((e): e is EstimatedBalance => !!e),
    [onboarded, byBranch],
  );
  const aggregate = useMemo(() => sumEstimatedBalances(estimates), [estimates]);

  // The per-platform split beneath the summed headline (clarity-balance-coverage
  // Req 6.2): roll each contributing branch's figure up by platform, flagging
  // whether it is REPORTED (Careem live balance) or ESTIMATED (Talabat/MyThings).
  const platformSplit = useMemo<PlatformContribution[]>(() => {
    const byPlatform = new Map<PlatformKey, PlatformContribution>();
    for (const b of onboarded) {
      const r = byBranch[b.id];
      if (!r || r.estimate === null || r.estimate.state !== 'available') continue;
      const amount = r.estimate.totalEstimatedOwed > 0 ? r.estimate.totalEstimatedOwed : r.estimate.confirmedOwed;
      const prev = byPlatform.get(r.platformKey);
      byPlatform.set(r.platformKey, {
        platformKey: r.platformKey,
        amount: (prev?.amount ?? 0) + amount,
        reported: r.reported || (prev?.reported ?? false),
      });
    }
    return [...byPlatform.values()].sort((a, b) => b.amount - a.amount);
  }, [onboarded, byBranch]);

  // Still waiting on at least one branch's first read.
  const loading =
    onboarded.length > 0 &&
    onboarded.some((b) => !byBranch[b.id] || byBranch[b.id]?.loading === true);

  return (
    <>
      {/* Hidden per-branch sources (multi-branch only; the single case reads inline). */}
      {single === null &&
        onboarded.map((b) => (
          <BranchBalanceSource key={b.id} branch={b} onResult={report} />
        ))}

      {single !== null ? (
        <SingleBranchLead branch={single} currency={currency} />
      ) : onboarded.length === 0 ? (
        <LeadShell state="unavailable">
          <p className="dash-bal__reason">No onboarded branch yet, so there&rsquo;s nothing to estimate.</p>
          <BestEffortNote asOf={null} />
        </LeadShell>
      ) : loading ? (
        <LeadShell state="loading">
          <p className="dash-bal__reason muted">Estimating across {onboarded.length} branches…</p>
        </LeadShell>
      ) : (
        <AggregateLead aggregate={aggregate} branchCount={onboarded.length} currency={currency} split={platformSplit} />
      )}
    </>
  );
}

/** The per-branch read a {@link BranchBalanceSource} reports up. */
interface BranchBalance {
  loading: boolean;
  /** The branch's estimate, or `null` while loading / on an expected 409 / error. */
  estimate: EstimatedBalance | null;
  /** The branch's platform, for the per-platform split + REPORTED vs ESTIMATED. */
  platformKey: PlatformKey;
  /** True when the figure is Careem's REPORTED live balance (not an estimate). */
  reported: boolean;
}

/**
 * Careem reports a live balance (`careem.liveBalance`) that the Talabat-style
 * `estimateBalance` never produces. Synthesize an `EstimatedBalance` from it so
 * the existing aggregation includes Careem (clarity-balance-coverage Req 1): the
 * reported `total` is the whole figure, carried as `confirmedOwed` +
 * `totalEstimatedOwed` with `state: 'available'`.
 */
function careemLiveEstimate(live: { total: number; underProcess: number; currency: string }): EstimatedBalance {
  return {
    state: 'available',
    confirmedOwed: live.total,
    sincePaid: 0,
    unpaidOrdersEstimate: 0,
    adsSinceStatement: 0,
    totalEstimatedOwed: Math.max(0, live.total),
    asOf: null,
    windowEnd: null,
    lastPayment: null,
    breakdown: { orders: [], ads: [] },
    upcomingPayout: null,
  };
}

/**
 * One hidden per-branch read feeding the multi-branch aggregate. Reads the
 * branch's derive summary and reports its figure up (clarity-balance-coverage):
 *  - Careem → the REPORTED `careem.liveBalance` (synthesized into an estimate);
 *  - Talabat / MyThings → `derived.estimatedBalance` (MyThings is now the
 *    earned-minus-paid estimate the server folds in).
 * The expected `409 onboarding_required` is NOT a failure — it reports none.
 * Renders nothing.
 */
function BranchBalanceSource({
  branch,
  onResult,
}: {
  branch: StoreResponse;
  onResult: (id: string, result: BranchBalance) => void;
}) {
  const summary = useDerivedSummary(branch.id);
  const blocked = isOnboardingRequired(summary.error);
  const derived = summary.data?.status === 200 && !blocked ? summary.data.body?.derived : null;
  const platformKey = branch.platformKey as PlatformKey;
  const live = platformKey === 'careem' ? (derived?.careem?.liveBalance ?? null) : null;
  const estimate = live !== null ? careemLiveEstimate(live) : (derived?.estimatedBalance ?? null);
  const reported = live !== null;
  const loading = summary.isLoading;

  useEffect(() => {
    onResult(branch.id, { loading, estimate, platformKey, reported });
  }, [branch.id, loading, estimate, platformKey, reported, onResult]);

  return null;
}

/** A platform's rolled-up contribution to the aggregate, for the split line. */
interface PlatformContribution {
  platformKey: PlatformKey;
  amount: number;
  reported: boolean;
}

/** The human platform name (matches the advisor/other surfaces). */
const PLATFORM_LABEL: Record<PlatformKey, string> = {
  talabat: 'Talabat',
  careem: 'Careem',
  mythings: 'MyThings',
};

/** The single-branch lead: reads one branch's estimate inline + full breakdown. */
function SingleBranchLead({
  branch,
  currency,
}: {
  branch: StoreResponse;
  currency: string | undefined;
}) {
  const summary = useDerivedSummary(branch.id);
  const blocked = isOnboardingRequired(summary.error);
  const cur = currency ?? branch.profile.currency;
  const platformKey = branch.platformKey as PlatformKey;

  if (summary.isLoading) {
    return (
      <LeadShell state="loading" platformKey={platformKey}>
        <p className="dash-bal__reason muted">Estimating…</p>
      </LeadShell>
    );
  }
  const derived = summary.data?.status === 200 && !blocked ? summary.data.body?.derived : null;
  const live = platformKey === 'careem' ? (derived?.careem?.liveBalance ?? null) : null;
  const estimate = live !== null ? careemLiveEstimate(live) : (derived?.estimatedBalance ?? null);
  if (estimate === null || estimate.state === 'unavailable') {
    return (
      <LeadShell state="unavailable" platformKey={platformKey}>
        <p className="dash-bal__reason">
          {estimate?.reason ?? 'Not enough data to estimate the balance yet.'}
        </p>
        <BestEffortNote asOf={null} />
      </LeadShell>
    );
  }

  return <SingleBranchBalance estimate={estimate} currency={cur} platformKey={platformKey} reported={live !== null} underProcess={live?.underProcess ?? null} />;
}

/**
 * Render ONE branch's estimate as the lead (the full headline + breakdown). This
 * mirrors the payouts {@link EstimatedBalanceCard} logic (same four honest
 * states, same confirmed/earned-since/less-ads parts, same best-effort
 * labelling) but in the compact summary-band lead layout.
 */
function SingleBranchBalance({
  estimate,
  currency,
  platformKey,
  reported,
  underProcess,
}: {
  estimate: EstimatedBalance;
  currency: string | undefined;
  platformKey: PlatformKey;
  /** True when the figure is Careem's REPORTED live balance (not an estimate). */
  reported: boolean;
  /** Careem's "being paid out now" amount, when reported; else null. */
  underProcess: number | null;
}) {
  const platformName = PLATFORM_LABEL[platformKey];
  const fmt = (n: number) => money(n, currency);
  // EXACT dates, not just the month. `asOf` is the statement period end (the
  // window's lower bound, shown on the "Confirmed by last statement" line);
  // `windowEnd` is the `now` the estimate was measured TO — the headline's
  // freshness is "as of" THAT (today/yesterday), not the stale statement date.
  const asOfDate = estimate.asOf ? shortDate(estimate.asOf) : null;
  const windowEndDate = estimate.windowEnd ? shortDate(estimate.windowEnd) : null;
  // How fresh the whole estimate is — the window end when there's a window, else
  // the statement date (the only anchor we have).
  const freshDate = windowEndDate ?? asOfDate;
  const confirmed = estimate.confirmedOwed;
  const unpaidOrders = estimate.unpaidOrdersEstimate ?? 0;
  const adsSince = estimate.adsSinceStatement ?? 0;
  const hasWindow = unpaidOrders > 0.005 || adsSince > 0.005;
  const headline = estimate.totalEstimatedOwed > 0.005 ? estimate.totalEstimatedOwed : confirmed;
  const owed = estimate.state === 'available' && headline > 0.005;
  const lastPayment = estimate.lastPayment ?? null;
  const breakdown = estimate.breakdown ?? { orders: [], ads: [] };
  const upcoming = estimate.upcomingPayout ?? null;

  const state: LeadState = owed
    ? 'owed'
    : estimate.state === 'unavailable'
      ? 'unavailable'
      : estimate.state === 'unconfirmed'
        ? 'unconfirmed'
        : 'clear';

  const showParts = !reported && hasWindow && (state === 'owed' || state === 'clear');

  return (
    <LeadShell state={state} platformKey={platformKey}>
      {state === 'owed' && (
        <>
          <p className="dash-bal__headline">
            {!reported && <span className="dash-bal__approx" aria-hidden="true">≈</span>}
            <span className="num dash-bal__amount">{fmt(headline)}</span>
          </p>
          <p className="dash-bal__caption">
            {reported
              ? `${platformName} reports this is your balance`
              : `${platformName} still owes you`}
            {freshDate ? <span className="dash-bal__asof"> · as of {freshDate}</span> : null}
          </p>
          {reported && underProcess !== null && underProcess > 0.005 && (
            <p className="dash-bal__note">{fmt(underProcess)} is already being paid out.</p>
          )}
        </>
      )}
      {state === 'clear' && (
        <>
          <p className="dash-bal__headline"><span className="dash-bal__amount dash-bal__amount--word">Settled</span></p>
          <p className="dash-bal__caption">
            {platformName} has settled everything — nothing outstanding
            {freshDate ? <span className="dash-bal__asof"> · as of {freshDate}</span> : null}
          </p>
        </>
      )}
      {state === 'unconfirmed' && (
        <>
          <p className="dash-bal__headline"><span className="dash-bal__amount dash-bal__amount--warn">Can&rsquo;t confirm</span></p>
          <p className="dash-bal__reason">
            The latest statement Clarity has{asOfDate ? <> is from {asOfDate}</> : null} and no newer
            payouts have synced — this isn&rsquo;t a confirmed zero.
          </p>
        </>
      )}
      {state === 'unavailable' && (
        <>
          <p className="dash-bal__headline"><span className="dash-bal__amount dash-bal__amount--muted">Not available</span></p>
          <p className="dash-bal__reason">{estimate.reason ?? 'Not enough statement data to estimate yet.'}</p>
        </>
      )}

      {showParts && (
        <dl className="dash-bal__parts">
          <div className="dash-bal__part">
            <dt>Confirmed by last statement{asOfDate ? <> ({asOfDate})</> : null}</dt>
            <dd className="num">{fmt(confirmed)}</dd>
          </div>
          {unpaidOrders > 0.005 && (
            <div className="dash-bal__part dash-bal__part--sub">
              <dt>
                Earned since, not yet paid
                {asOfDate && windowEndDate ? (
                  <span className="dash-bal__range"> ({asOfDate} → {windowEndDate})</span>
                ) : null}
              </dt>
              <dd className="num">+ {fmt(unpaidOrders)}</dd>
            </div>
          )}
          {adsSince > 0.005 && (
            <div className="dash-bal__part dash-bal__part--deduct">
              <dt>Less ads spent since</dt>
              <dd className="num">− {fmt(adsSince)}</dd>
            </div>
          )}
        </dl>
      )}
      {upcoming !== null && <UpcomingPayoutBlock upcoming={upcoming} currency={currency} />}
      {lastPayment !== null && (
        // The last payment Talabat actually made — date, amount, AND the earning
        // period it covered (a payout settles a window, paid days later) — so the
        // owner sees when they were last paid and for what, distinct from the owed.
        <p className="dash-bal__lastpay">
          <span>
            Last payment · {shortDate(lastPayment.paidAt)}
            <span className="dash-bal__range">
              {' '}· for {shortDate(lastPayment.periodStart)} → {shortDate(lastPayment.periodEnd)}
            </span>
          </span>
          <span className="num dash-bal__lastpay-amt">{fmt(lastPayment.amount)}</span>
        </p>
      )}
      {state === 'clear' && adsSince > 0.005 && (
        <p className="dash-bal__note">
          Ads spent since the last statement ({fmt(adsSince)}) are accounted for — they net the balance to zero here.
        </p>
      )}
      {/* The full itemized breakdown — exactly which orders + ads make the figure. */}
      {showParts && (breakdown.orders.length > 0 || breakdown.ads.length > 0) && (
        <BalanceDetails breakdown={breakdown} currency={currency} />
      )}
      {reported
        ? (state === 'owed' || state === 'clear') && (
            <p className="dash-bal__caveat">Reported by {platformName} — not a Clarity estimate</p>
          )
        : (state === 'owed' || state === 'clear') && (
            <BestEffortNote asOf={freshDate} sinceCaveat={state === 'owed' && hasWindow} />
          )}
    </LeadShell>
  );
}

/**
 * The inline-expanding "See full breakdown" — the exact orders and payout-ads the
 * post-statement window figure is summed from (clarity-daily-update Req 5.7), so
 * the owner can trace "how Talabat owes you this" line by line. Collapsed by
 * default; the trigger is a real button with `aria-expanded`. The itemized totals
 * equal the headline terms by construction (same core window/filter).
 */
function BalanceDetails({
  breakdown,
  currency,
}: {
  breakdown: EstimatedBalance['breakdown'];
  currency: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const fmt = (n: number) => money(n, currency);
  const orders = breakdown.orders;
  const ads = breakdown.ads;

  return (
    <div className="dash-bal__details">
      <button
        type="button"
        className="dash-bal__details-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide breakdown' : 'See full breakdown'}
        <span className="dash-bal__details-hint">
          {' '}
          ({orders.length} order{orders.length === 1 ? '' : 's'}
          {ads.length > 0 ? `, ${ads.length} ad charge${ads.length === 1 ? '' : 's'}` : ''})
        </span>
      </button>

      {open && (
        <div className="dash-bal__details-body">
          {orders.length > 0 && (
            <section aria-label="Orders earned since, not yet paid">
              <h4 className="dash-bal__details-head">Earned since, not yet paid</h4>
              <table className="data-table dash-bal__details-table">
                <thead>
                  <tr>
                    <th scope="col">Order</th>
                    <th scope="col">Placed</th>
                    <th scope="col" className="num">Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className={o.cancelled ? 'dash-bal__details-row--cancelled' : undefined}>
                      <td>
                        {o.externalOrderId}
                        {o.cancelled ? <span className="dash-bal__details-tag"> cancelled</span> : null}
                      </td>
                      <td className="num">{shortDate(o.timestamp)}</td>
                      <td className="num">{fmt(o.earnings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {ads.length > 0 && (
            <section aria-label="Ads spent since the statement">
              <h4 className="dash-bal__details-head">Ads spent since (deducted)</h4>
              <table className="data-table dash-bal__details-table">
                <thead>
                  <tr>
                    <th scope="col">Payout</th>
                    <th scope="col">Period</th>
                    <th scope="col" className="num">Ads</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map((a) => (
                    <tr key={a.externalPayoutId}>
                      <td>{a.externalPayoutId}</td>
                      <td className="num">
                        {shortDate(a.periodStart)} → {shortDate(a.periodEnd)}
                      </td>
                      <td className="num dash-bal__details-deduct">− {fmt(a.ads)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The "Next payout" block (clarity-payout-schedule Req 4.1, 4.2) — the next
 * scheduled payout date, the coverage window it settles, and a best-effort amount
 * (a SLICE of what's owed, not extra money). A `monthly_closing` row reads
 * "settles the month" instead of a number. A "Payout today" chip shows when the
 * payout lands on the store's local today.
 */
function UpcomingPayoutBlock({
  upcoming,
  currency,
}: {
  upcoming: UpcomingPayout;
  currency: string | undefined;
}) {
  const fmt = (n: number) => money(n, currency);
  const isClosing = upcoming.type === 'monthly_closing';
  const cover =
    !isClosing && upcoming.coverageStart && upcoming.coverageEnd
      ? `covers ${shortDate(upcoming.coverageStart)} → ${shortDate(upcoming.coverageEnd)}`
      : 'settles the month';
  const notes = upcomingNotes(upcoming, fmt);
  return (
    <div className="dash-bal__upcoming" data-today={upcoming.dueToday ? 'yes' : 'no'}>
      <div className="dash-bal__upcoming-head">
        <span className="dash-bal__upcoming-title">Next payout</span>
        {upcoming.dueToday && (
          <span className="dash-bal__today-chip">
            <span className="dash-bal__today-dot" aria-hidden="true" />
            Payout today
          </span>
        )}
      </div>
      <div className="dash-bal__upcoming-body">
        <div>
          <div className="dash-bal__upcoming-when num">{shortDate(upcoming.payoutDate)}</div>
          <div className="dash-bal__upcoming-cover">{cover}</div>
        </div>
        <div className="dash-bal__upcoming-amt">
          {isClosing || upcoming.amount === null ? (
            <span className="dash-bal__upcoming-closing">settles the month</span>
          ) : (
            <>
              <span className="num">≈ {fmt(upcoming.amount)}</span>
              <small>of what&rsquo;s owed, estimated</small>
              {notes.carried && <small>{notes.carried}</small>}
            </>
          )}
        </div>
      </div>
      {notes.minimum && (
        <p className="dash-bal__upcoming-min" data-testid="upcoming-below-minimum">{notes.minimum}</p>
      )}
    </div>
  );
}

/** The multi-branch aggregate lead: the summed headline + the unavailable caveat. */
function AggregateLead({
  aggregate,
  branchCount,
  currency,
  split,
}: {
  aggregate: EstimatedBalanceAggregate;
  branchCount: number;
  currency: string | undefined;
  split: PlatformContribution[];
}) {
  const fmt = (n: number) => money(n, currency);
  const asOfDate = aggregate.asOf ? shortDate(aggregate.asOf) : null;
  const headline = aggregate.totalEstimatedOwed;
  const owed = aggregate.state === 'available' && headline > 0.005;
  const state: LeadState = owed ? 'owed' : aggregate.state === 'available' ? 'clear' : 'unavailable';

  return (
    <LeadShell state={state}>
      {owed ? (
        <>
          <p className="dash-bal__headline">
            <span className="dash-bal__approx" aria-hidden="true">≈</span>
            <span className="num dash-bal__amount">{fmt(headline)}</span>
          </p>
          <p className="dash-bal__caption">
            The platforms still owe these stores
            {asOfDate ? <span className="dash-bal__asof"> · as of {asOfDate}</span> : null}
          </p>
        </>
      ) : state === 'clear' ? (
        <>
          <p className="dash-bal__headline"><span className="dash-bal__amount dash-bal__amount--word">Settled</span></p>
          <p className="dash-bal__caption">Nothing outstanding across these stores right now</p>
        </>
      ) : (
        <>
          <p className="dash-bal__headline"><span className="dash-bal__amount dash-bal__amount--muted">Not available</span></p>
          <p className="dash-bal__reason">No branch has enough data to estimate yet.</p>
        </>
      )}
      {owed && split.length > 1 && (
        <dl className="dash-bal__parts dash-bal__split">
          {split.map((p) => (
            <div key={p.platformKey} className="dash-bal__part">
              <dt>
                {PLATFORM_LABEL[p.platformKey]}
                <span className="dash-bal__badge">{p.reported ? 'reported' : 'estimated'}</span>
              </dt>
              <dd className="num">{fmt(p.amount)}</dd>
            </div>
          ))}
        </dl>
      )}
      {aggregate.unavailableCount > 0 && aggregate.state === 'available' && (
        <p className="dash-bal__scopecaveat">
          + {aggregate.unavailableCount} branch{aggregate.unavailableCount === 1 ? '' : 'es'} couldn&rsquo;t be estimated
        </p>
      )}
      {(owed || state === 'clear') && <BestEffortNote asOf={asOfDate} summed={branchCount} />}
    </LeadShell>
  );
}

type LeadState = 'owed' | 'clear' | 'unconfirmed' | 'unavailable' | 'loading';

/**
 * The lead block shell: the label + status dot + the state tint, wrapping the
 * content. `platformKey` names a single-platform card ("… owed by Careem");
 * omitted ⇒ the platform-neutral multi-platform label (clarity-balance-coverage
 * Req 6.1).
 */
function LeadShell({
  state,
  platformKey,
  children,
}: {
  state: LeadState;
  platformKey?: PlatformKey;
  children: React.ReactNode;
}) {
  const label = platformKey ? `Estimated balance owed by ${PLATFORM_LABEL[platformKey]}` : 'Estimated balance owed';
  return (
    <div className="summary-band__lead dash-bal" data-state={state} role="region" aria-label={label}>
      <span className="dash-bal__label">
        <span className="dash-bal__dot" aria-hidden="true" />
        {label}
      </span>
      {children}
    </div>
  );
}

/** The best-effort caveat line — always present on a figure so it's never read as settled truth. */
function BestEffortNote({
  asOf,
  sinceCaveat,
  summed,
}: {
  asOf: string | null;
  sinceCaveat?: boolean;
  summed?: number;
}) {
  return (
    <p className="dash-bal__caveat">
      Best-effort estimate{asOf ? <> · as of {asOf}</> : null}
      {summed ? <> · summed across {summed} branches</> : null}
      {sinceCaveat ? <> · the &ldquo;since&rdquo; part isn&rsquo;t settled by Talabat yet</> : null}
    </p>
  );
}
